import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router';
import { ChevronDown, KeyRound, Laptop, LogOut, Menu } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import styles from './Topbar.module.css';

export interface TopbarProps {
  onMenuToggle: () => void;
  menuButtonRef?: RefObject<HTMLButtonElement | null>;
}

export function Topbar({ onMenuToggle, menuButtonRef }: TopbarProps) {
  const { user, logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);

  const closeAccount = useCallback((restoreFocus = true) => {
    setAccountOpen(false);
    if (restoreFocus) queueMicrotask(() => accountButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!accountOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) closeAccount(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAccount();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    queueMicrotask(() => accountRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen, closeAccount]);

  return (
    <header className={styles.topbar}>
      <div className={styles.left}>
        <button
          ref={menuButtonRef}
          className={styles.hamburger}
          onClick={onMenuToggle}
          aria-label="Abrir menu"
        >
          <Menu size={22} strokeWidth={1.5} />
        </button>
      </div>

      <div className={styles.right} ref={accountRef}>
        <button
          ref={accountButtonRef}
          type="button"
          className={styles.accountButton}
          aria-haspopup="menu"
          aria-expanded={accountOpen}
          onClick={() => setAccountOpen((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setAccountOpen(true);
            }
          }}
        >
          <span className={styles.avatar} aria-hidden="true">
            {user?.name
              ? user.name
                  .split(' ')
                  .slice(0, 2)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
              : '?'}
          </span>
          <span className={styles.userName}>{user?.name}</span>
          <span className={styles.accountLabel}>Minha conta</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>

        {accountOpen && (
          <div
            className={styles.accountMenu}
            role="menu"
            aria-label="Minha conta"
            onKeyDown={(event) => {
              if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const items = Array.from(
                event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
              );
              if (items.length === 0) return;
              const current = items.indexOf(document.activeElement as HTMLElement);
              const next = event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? items.length - 1
                  : event.key === 'ArrowDown'
                    ? (current + 1) % items.length
                    : (current - 1 + items.length) % items.length;
              items[next]?.focus();
            }}
          >
            <Link role="menuitem" to="/change-password" onClick={() => closeAccount(false)}>
              <KeyRound size={16} aria-hidden="true" />
              Alterar senha
            </Link>
            <Link role="menuitem" to="/sessions" onClick={() => closeAccount(false)}>
              <Laptop size={16} aria-hidden="true" />
              Sessões ativas
            </Link>
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                closeAccount(false);
                logout();
              }}
            >
              <LogOut size={16} aria-hidden="true" />
              Sair
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
